import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { api, auth } from '../api/client';
import type {
  AnswerPayload, AnswerSheetConfig, Attempt, DescriptiveAnswer, ExamPaper, McqAnswer, Question
} from '../types/models';
import { fnv1a } from '../utils/hash';
import {
  descriptiveAnswered, emptyDescriptive, emptyDescriptiveForParts, normalizeDescriptive
} from '../utils/answers';

type AnswersMap = Record<string, AnswerPayload>;

function emptyAnswerFor(q: Question, cfg?: AnswerSheetConfig | null): AnswerPayload {
  if (q.type !== 'Descriptive') return { kind: 'mcq', selectedOptionIds: [] };
  if (q.parts && q.parts.length > 0) return emptyDescriptiveForParts(q.parts, q.allowText, cfg);
  return emptyDescriptive(q.allowText, cfg);
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const HUB_URL = (import.meta as any).env?.VITE_HUB_URL || '/hubs/exam';

export function useExamSession(examId: string, active: boolean) {
  const [paper, setPaper] = useState<ExamPaper | null>(null);
  const [attempt, setAttempt] = useState<Attempt | null>(null);
  const [answers, setAnswers] = useState<AnswersMap>({});
  const [remaining, setRemaining] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<Record<string, SaveStatus>>({});

  const lastSavedHashRef = useRef<Record<string, string>>({});
  const pendingRef = useRef<Record<string, number>>({});
  const hubRef = useRef<signalR.HubConnection | null>(null);

  // --- load the paper up front (so the instructions/rules screen can render) --------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = await api.getPaper(examId);
        if (!cancelled) setPaper(p);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message ?? 'Failed to load exam.');
      }
    })();
    return () => { cancelled = true; };
  }, [examId]);

  // --- start the attempt + load existing answers only once the student begins. This is what
  // starts the (wall-clock) timer, so it is deferred until they acknowledge the rules. ------
  useEffect(() => {
    if (!active || !paper || attempt) return;
    let cancelled = false;
    (async () => {
      try {
        const a = await api.startAttempt(examId);
        const existing = await api.getAnswers(a.id);
        const seed: AnswersMap = {};
        for (const q of paper.questions) seed[q.id] = emptyAnswerFor(q, paper.answerSheets);
        for (const rec of existing) {
          try {
            const parsed = JSON.parse(rec.contentJson) as any;
            if (parsed?.kind === 'descriptive') {
              seed[rec.questionId] = normalizeDescriptive(parsed);
              lastSavedHashRef.current[rec.questionId] = fnv1a(rec.contentJson);
            } else if (parsed?.kind === 'mcq') {
              seed[rec.questionId] = parsed;
              lastSavedHashRef.current[rec.questionId] = fnv1a(rec.contentJson);
            }
          } catch { /* ignore malformed cached entries */ }
        }
        if (cancelled) return;
        setAnswers(seed);
        setAttempt(a);
        setRemaining(a.remainingSeconds);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message ?? 'Failed to start exam.');
      }
    })();
    return () => { cancelled = true; };
  }, [active, paper, attempt, examId]);

  // --- local countdown (authoritative from server on reconnect) ---------------------------
  // We only count down to zero here; the page reacts to `remaining === 0` and drives the
  // auto-submit (so the UI flips to the "submitted" screen too).
  useEffect(() => {
    if (!attempt) return;
    const id = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) {
          clearInterval(id);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [attempt?.id]);

  // --- SignalR hub: heartbeats + proctor events -------------------------------------------
  useEffect(() => {
    if (!attempt) return;
    const token = auth.getToken();
    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${HUB_URL}?access_token=${encodeURIComponent(token ?? '')}`)
      .withAutomaticReconnect()
      .build();
    hubRef.current = conn;

    conn.start().then(() => conn.invoke('JoinAttempt', attempt.id)).catch(() => { /* nonfatal */ });

    const hbTimer = setInterval(() => {
      if (conn.state === signalR.HubConnectionState.Connected) {
        conn.invoke('Heartbeat', attempt.id).catch(() => { /* nonfatal */ });
      } else {
        api.heartbeat(attempt.id).catch(() => { /* nonfatal */ });
      }
    }, 30_000);

    const onFocusLoss = () => {
      conn.invoke('ReportFocusEvent', attempt.id, 'visibility', document.visibilityState).catch(() => { });
    };
    document.addEventListener('visibilitychange', onFocusLoss);

    return () => {
      clearInterval(hbTimer);
      document.removeEventListener('visibilitychange', onFocusLoss);
      conn.stop();
    };
  }, [attempt?.id]);

  // --- debounced auto-save per question ---------------------------------------------------
  const scheduleSave = useCallback((questionId: string, payload: AnswerPayload) => {
    if (!attempt) return;
    const json = JSON.stringify(payload);
    const hash = fnv1a(json);
    if (lastSavedHashRef.current[questionId] === hash) return;

    if (pendingRef.current[questionId]) {
      clearTimeout(pendingRef.current[questionId]);
    }
    setSaveStatus(s => ({ ...s, [questionId]: 'saving' }));
    pendingRef.current[questionId] = window.setTimeout(async () => {
      try {
        await api.upsertAnswer(attempt.id, questionId, json, hash);
        lastSavedHashRef.current[questionId] = hash;
        setSaveStatus(s => ({ ...s, [questionId]: 'saved' }));
      } catch {
        setSaveStatus(s => ({ ...s, [questionId]: 'error' }));
      }
    }, 700);
  }, [attempt?.id]);

  const setAnswer = useCallback((questionId: string, next: AnswerPayload) => {
    setAnswers(prev => ({ ...prev, [questionId]: next }));
    scheduleSave(questionId, next);
  }, [scheduleSave]);

  const submit = useCallback(async (auto = false) => {
    if (!attempt) return null;
    // Flush any pending debounced saves first.
    const flushes = Object.entries(pendingRef.current).map(([qid, handle]) => {
      clearTimeout(handle);
      const payload = answers[qid];
      if (!payload) return Promise.resolve();
      const json = JSON.stringify(payload);
      return api.upsertAnswer(attempt.id, qid, json, fnv1a(json)).catch(() => { });
    });
    await Promise.all(flushes);
    return api.submitAttempt(attempt.id, auto);
  }, [attempt?.id, answers]);

  const answered = useMemo(() => {
    const set = new Set<string>();
    for (const [qid, p] of Object.entries(answers)) {
      if (p.kind === 'mcq' && p.selectedOptionIds.length > 0) set.add(qid);
      else if (p.kind === 'descriptive' && descriptiveAnswered(p)) set.add(qid);
    }
    return set;
  }, [answers]);

  return {
    paper, attempt, answers, remaining,
    setAnswer, submit, loadError, saveStatus, answered,
    // Cast so pages can use narrower types at the call site. Fall back to an empty payload
    // if the answers map hasn't caught up yet — prevents undefined crashes during
    // intermediate renders.
    mcqOf: (qid: string): McqAnswer => {
      const v = answers[qid];
      return v && v.kind === 'mcq' ? v : { kind: 'mcq', selectedOptionIds: [] };
    },
    descOf: (qid: string): DescriptiveAnswer => {
      const v = answers[qid];
      return v && v.kind === 'descriptive' ? v : emptyDescriptive(false);
    }
  };
}
